/**
 * Home and menu layout: two lists the person orders freely (Settings › Home, menu and
 * start). Home's shortcuts are any pages; the menu lists Home and any pages. A page can
 * be in either list, both, or neither ("Not shown": reachable by link or as the start
 * page). This is the web's first take; natively Home and the menu still split the pages
 * between them (`HomeLayout`, apps/ios-native/Sources/AppNavigation.swift), and the
 * native app follows once this proves out.
 *
 * Lists belong to the account profile. The legacy device value "2:home,…|menu,…"
 * is read only for one-time adoption; start page remains device-local. A legacy string
 * without "2:" reads as the earlier split layout, with Home leading the menu.
 *
 * Pure: no React, no storage. See docs/stasht/web_navigation_inventory.md.
 */

/** Pages people place on Home or in the menu, by their native identity. */
export const PLACEABLE_PAGES = [
  "faves",
  "done",
  "all-stashes",
  "tags",
  "collections",
  "roundups",
  "calendar",
  "map",
  "discover",
  "chat",
] as const;

export type PlaceablePage = (typeof PLACEABLE_PAGES)[number];

/** Home in the menu (and as the start page), by its native identity. Never a shortcut. */
export const HOME_PAGE = "stashes";
export type MenuPage = PlaceablePage | typeof HOME_PAGE;
const MENU_PAGES: readonly MenuPage[] = [HOME_PAGE, ...PLACEABLE_PAGES];
export type LayoutGroup = "home" | "menu";

export interface HomeLayout {
  home: PlaceablePage[];
  menu: MenuPage[];
}

export const LAYOUT_KEY = "navigation.homeLayout";
export const START_PAGE_KEY = "navigation.startPage";

/** Home's first pages are cards; the rest are shortcuts. */
export const CARD_COUNT = 2;

const FORMAT = "2:";

/** Chat and Map lead because both have something to show before the first save. */
export const STANDARD_HOME: readonly PlaceablePage[] = ["chat", "map", "discover", "collections", "tags", "all-stashes"];
export const STANDARD_MENU: readonly MenuPage[] = [HOME_PAGE, "faves", "done", "roundups", "calendar"];

const isPlaceable = (id: string): id is PlaceablePage => (PLACEABLE_PAGES as readonly string[]).includes(id);
const isMenuPage = (id: string): id is MenuPage => id === HOME_PAGE || isPlaceable(id);

/** Known pages, each once, in the order given. */
function unique<T extends string>(ids: readonly string[], known: (id: string) => id is T): T[] {
  const kept: T[] = [];
  for (const id of ids) if (known(id) && !kept.includes(id)) kept.push(id);
  return kept;
}

const standard = (): HomeLayout => ({ home: [...STANDARD_HOME], menu: [...STANDARD_MENU] });

/** Never fails: whatever is stored (or nothing) reads into a layout. */
export function parseLayout(raw: string | null | undefined): HomeLayout {
  if (!raw) return standard();
  const current = raw.startsWith(FORMAT);
  const [home = "", menu = ""] = (current ? raw.slice(FORMAT.length) : raw).split("|");
  const ids = (group: string) => group.split(",").filter(Boolean);
  const layout: HomeLayout = { home: unique(ids(home), isPlaceable), menu: unique(ids(menu), isMenuPage) };
  if (!current) {
    // The split layout had Home first in the menu and every other page in one of the two.
    const missing = PLACEABLE_PAGES.filter(page => !layout.home.includes(page) && !layout.menu.includes(page));
    layout.menu = unique([HOME_PAGE, ...layout.menu, ...missing], isMenuPage);
  }
  return layout;
}

export const serializeLayout = (layout: HomeLayout): string =>
  `${FORMAT}${layout.home.join(",")}|${layout.menu.join(",")}`;

export const isStandardLayout = (layout: HomeLayout): boolean =>
  serializeLayout(layout) === serializeLayout(standard());

export const cardsOf = (layout: HomeLayout): PlaceablePage[] => layout.home.slice(0, CARD_COUNT);
export const shortcutsOf = (layout: HomeLayout): PlaceablePage[] => layout.home.slice(CARD_COUNT);

/** The pages a list can still take, in the app's page order. */
export const addablePages = (layout: HomeLayout, group: LayoutGroup): MenuPage[] =>
  group === "home"
    ? PLACEABLE_PAGES.filter(page => !layout.home.includes(page))
    : MENU_PAGES.filter(page => !layout.menu.includes(page));

/** Pages on neither list. */
export const notShownPages = (layout: HomeLayout): MenuPage[] =>
  MENU_PAGES.filter(page => !layout.menu.includes(page) && !(isPlaceable(page) && layout.home.includes(page)));

/** Adds a page at the end of a list. */
export function addPage(layout: HomeLayout, group: LayoutGroup, page: MenuPage): HomeLayout {
  if (group === "home") {
    return isPlaceable(page) && !layout.home.includes(page) ? { ...layout, home: [...layout.home, page] } : layout;
  }
  return layout.menu.includes(page) ? layout : { ...layout, menu: [...layout.menu, page] };
}

export function removePage(layout: HomeLayout, group: LayoutGroup, page: MenuPage): HomeLayout {
  return group === "home"
    ? { ...layout, home: layout.home.filter(p => p !== page) }
    : { ...layout, menu: layout.menu.filter(p => p !== page) };
}

/**
 * Puts a list's pages in `order`. Its pages missing from `order` (ones the web doesn't
 * list yet) follow, as they were; pages not in the list are ignored.
 */
export function reorderGroup(layout: HomeLayout, group: LayoutGroup, order: readonly MenuPage[]): HomeLayout {
  const pages: MenuPage[] = group === "home" ? layout.home : layout.menu;
  const listed = order.filter(page => pages.includes(page));
  const next = [...listed, ...pages.filter(page => !listed.includes(page))];
  return group === "home" ? { ...layout, home: next.filter(isPlaceable) } : { ...layout, menu: next };
}

/** The start page: Home (null) or any page, wherever it's placed. */
export const resolveStartPage = (stored: string | null | undefined): PlaceablePage | null =>
  stored && isPlaceable(stored) ? stored : null;

/** Profile ownership is checked at each async boundary, including optimistic rollback. */
export interface LayoutProfile { user_id: string; home_layout?: HomeLayout | null }
export const layoutForProfile = (profile: LayoutProfile | undefined, userId: string | null, legacy?: string | null): HomeLayout =>
  profile?.user_id === userId ? profile.home_layout ?? parseLayout(legacy) : standard();

/** Only a freshly fetched matching profile may authorize adopting this device's legacy value. */
export const legacyLayoutToAdopt = (profile: LayoutProfile, userId: string | null, legacy: string | null): HomeLayout | undefined =>
  profile.user_id === userId && profile.home_layout == null && legacy !== null ? parseLayout(legacy) : undefined;

export const confirmsLayout = (profile: LayoutProfile, userId: string, requested: HomeLayout | null): boolean =>
  profile.user_id === userId && (requested === null
    ? profile.home_layout === null
    : profile.home_layout != null && serializeLayout(profile.home_layout) === serializeLayout(requested));

/** Patch one account field; unrelated preference changes survive success and rollback. */
export function patchProfileLayout<T extends LayoutProfile>(profile: T | undefined, userId: string, layout: HomeLayout | null | undefined): T | undefined {
  return profile?.user_id === userId ? { ...profile, home_layout: layout } : profile;
}
